import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from 'react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Box,
  Chip,
  TextField,
  InputAdornment,
  TablePagination,
  Button,
  Snackbar,
} from '@material-ui/core';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  CheckCircle as CheckIcon,
  SwapHoriz as SwapHorizIcon,
  ArrowForward as ArrowForwardIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import MuiAlert from '@material-ui/lab/Alert';
import { debounce } from 'lodash';
import { useAuth } from '../../../../../auth/authProvider';
import partnershipRequest from '../../../../../services/network/partnershipRequest';

const useStyles = makeStyles({
  dialog: {
    minWidth: 700,
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: 8,
  },
  closeButton: {
    marginRight: -8,
  },
  searchContainer: {
    marginBottom: 16,
  },
  searchField: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
    },
  },
  tableContainer: {
    maxHeight: 400,
    overflow: 'auto',
  },
  emptyMessage: {
    textAlign: 'center',
    padding: 32,
    color: '#666',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 48,
  },
  statusActive: {
    backgroundColor: '#4caf50',
    color: '#fff',
  },
  statusInactive: {
    backgroundColor: '#f44336',
    color: '#fff',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 8,
  },
  paginationRoot: {
    '& .MuiTablePagination-toolbar': {
      paddingLeft: 0,
    },
  },
  linkedChip: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    fontWeight: 600,
  },
  otherPlanChip: {
    backgroundColor: '#fff3e0',
    color: '#FF5246',
    fontWeight: 500,
    fontSize: 11,
  },
  searchResultRow: {
    backgroundColor: '#f5f5f5',
    '&:hover': {
      backgroundColor: '#eeeeee',
    },
  },
  linkedRow: {},
  confirmDialog: {
    minWidth: '400px',
  },
  confirmContent: {
    padding: '24px',
  },
  helperText: {
    color: '#666',
    fontSize: 12,
    marginTop: 4,
  },
});

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

const formatPhone = phone => {
  if (!phone) {
    return '-';
  }
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 11) {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
  }
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
  }
  return phone;
};

const formatStatus = status => {
  const statusMap = {
    ACTIVE: 'Ativo',
    INACTIVE: 'Inativo',
  };
  return statusMap[status] || status;
};

export function ModalGerenciarParceiros({ open, onClose, planData }) {
  const classes = useStyles();
  const { token } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [linkingPartnerId, setLinkingPartnerId] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [partnerToConfirm, setPartnerToConfirm] = useState(null);

  const planNumericId = planData?.id;

  const { data: linkedPartners = [], isLoading, refetch } = useQuery(
    ['fetch-partners-by-plan', planNumericId],
    async () => {
      if (!planNumericId) {
        return [];
      }
      const params = {
        filters: JSON.stringify({ comissionPlan: { id: planNumericId } }),
        relations: JSON.stringify(['comissionPlan']),
        limit: 500,
      };
      const response = await partnershipRequest.get('partners/partner', { token, params });
      if (Array.isArray(response)) {
        return response;
      }
      if (response?.data) {
        return response.data;
      }
      if (response?.content) {
        return response.content;
      }
      return [];
    },
    {
      enabled: open && !!planNumericId,
    },
  );

  const fetchSearchResults = async name => {
    setIsSearching(true);
    try {
      const filters = [{ fullName: name }, { tradeName: name }, { businessName: name }];
      const params = {
        filters: JSON.stringify(filters),
        relations: JSON.stringify(['comissionPlan']),
      };
      const partnersData = await partnershipRequest.get('partners/partner', { token, params });
      let results = [];
      if (partnersData && Array.isArray(partnersData) && partnersData.length > 0) {
        results = partnersData;
      } else if (partnersData && (partnersData.data || partnersData.content)) {
        const data = partnersData.data || partnersData.content;
        results = Array.isArray(data) ? data : [];
      }
      setSearchResults(results);
    } catch (error) {
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const debouncedSearch = useMemo(
    () =>
      debounce(value => {
        if (value.length >= 3) {
          fetchSearchResults(value);
        } else {
          setSearchResults([]);
        }
      }, 500),
    [token],
  );

  const handleSearchChange = event => {
    const { value } = event.target;
    setSearchTerm(value);
    setPage(0);
    if (value.length >= 3) {
      debouncedSearch(value);
    } else {
      setSearchResults([]);
    }
  };

  const displayedPartners = useMemo(() => {
    if (searchTerm.length >= 3) {
      return searchResults;
    }
    if (!searchTerm.trim()) {
      return linkedPartners;
    }
    const lowerSearch = searchTerm.toLowerCase();
    return linkedPartners.filter(
      partner =>
        (partner.fullName && partner.fullName.toLowerCase().includes(lowerSearch)) ||
        (partner.businessName && partner.businessName.toLowerCase().includes(lowerSearch)),
    );
  }, [linkedPartners, searchResults, searchTerm]);

  const paginatedPartners = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return displayedPartners.slice(startIndex, startIndex + rowsPerPage);
  }, [displayedPartners, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusClass = status => {
    return status === 'ACTIVE' ? classes.statusActive : classes.statusInactive;
  };

  const isLinkedToThisPlan = partner => {
    if (!partner.comissionPlan || !planData) {
      return false;
    }
    const partnerPlanId = partner.comissionPlan.id || partner.comissionPlan.publicId;
    const currentPlanId = planData.id || planData.publicId;
    return partnerPlanId === currentPlanId;
  };

  const isLinkedToOtherPlan = partner => {
    if (!partner.comissionPlan) {
      return false;
    }
    return !isLinkedToThisPlan(partner);
  };

  const getPartnerCurrentPlanName = partner => {
    if (!partner.comissionPlan) {
      return null;
    }
    return partner.comissionPlan.name || 'Plano sem nome';
  };

  const getPartnerDisplayName = partner => {
    return partner.fullName || partner.businessName || partner.tradeName || 'Parceiro';
  };

  const handleLinkPartner = async partnerId => {
    if (!planData || (!planData.id && !planData.publicId)) {
      setErrorMessage('Plano não encontrado');
      setShowErrorMessage(true);
      return;
    }
    setLinkingPartnerId(partnerId);
    try {
      const comissionPlanId = planData.id || planData.publicId;
      await partnershipRequest.patch(
        `partners/partner/${partnerId}`,
        { comissionPlan: { id: comissionPlanId } },
        { token },
      );
      setShowSuccessMessage(true);
      refetch();
      if (searchTerm.length >= 3) {
        fetchSearchResults(searchTerm);
      }
    } catch (error) {
      const message = error || 'Erro ao vincular parceiro ao plano';
      setErrorMessage(message);
      setShowErrorMessage(true);
    } finally {
      setLinkingPartnerId(null);
    }
  };

  const handleLinkClick = partner => {
    const partnerId = partner.id || partner.accountPublicId;
    if (isLinkedToOtherPlan(partner)) {
      setPartnerToConfirm(partner);
      setConfirmDialogOpen(true);
      return;
    }
    handleLinkPartner(partnerId);
  };

  const handleConfirmLink = async () => {
    if (!partnerToConfirm) {
      return;
    }
    const partnerId = partnerToConfirm.id || partnerToConfirm.accountPublicId;
    setConfirmDialogOpen(false);
    await handleLinkPartner(partnerId);
    setPartnerToConfirm(null);
  };

  const handleCancelConfirm = () => {
    setConfirmDialogOpen(false);
    setPartnerToConfirm(null);
  };

  const handleCloseSnackbar = () => {
    setShowSuccessMessage(false);
    setShowErrorMessage(false);
  };

  const handleClose = () => {
    setSearchTerm('');
    setSearchResults([]);
    setPage(0);
    setLinkingPartnerId(null);
    setShowSuccessMessage(false);
    setShowErrorMessage(false);
    setConfirmDialogOpen(false);
    setPartnerToConfirm(null);
    onClose();
  };

  const renderPartnerAction = partner => {
    const partnerId = partner.id || partner.accountPublicId;
    const isLinking = linkingPartnerId === partnerId;
    const linkedToThis = isLinkedToThisPlan(partner);
    const linkedToOther = isLinkedToOtherPlan(partner);

    if (linkedToThis) {
      return (
        <Chip
          icon={<CheckIcon style={{ color: '#2e7d32' }} />}
          label="Vinculado"
          className={classes.linkedChip}
          size="small"
        />
      );
    }

    if (linkedToOther) {
      return (
        <Button
          variant="outlined"
          size="small"
          onClick={() => handleLinkClick(partner)}
          disabled={isLinking}
          style={{ borderColor: '#FF5246', color: '#FF5246', fontSize: 11 }}
          startIcon={isLinking ? <CircularProgress size={14} /> : <SwapHorizIcon style={{ fontSize: 16 }} />}
        >
          {isLinking ? 'Alterando...' : 'Alterar'}
        </Button>
      );
    }

    return (
      <Button
        variant="contained"
        color="primary"
        size="small"
        onClick={() => handleLinkClick(partner)}
        disabled={isLinking}
        style={{ fontSize: 11 }}
      >
        {isLinking ? <CircularProgress size={16} style={{ color: '#fff' }} /> : 'Vincular'}
      </Button>
    );
  };

  const getRowClassName = partner => {
    if (isLinkedToThisPlan(partner)) {
      return classes.linkedRow;
    }
    if (searchTerm.length >= 3 && !isLinkedToThisPlan(partner)) {
      return classes.searchResultRow;
    }
    return '';
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box className={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      );
    }
    return (
      <>
        <Box className={classes.searchContainer}>
          <TextField
            className={classes.searchField}
            placeholder="Buscar parceiro por nome ou razão social..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: isSearching ? (
                <InputAdornment position="end">
                  <CircularProgress size={20} />
                </InputAdornment>
              ) : null,
            }}
          />
          <Typography className={classes.helperText}>
            {searchTerm.length >= 3
              ? 'Mostrando resultados da busca. Parceiros não vinculados podem ser adicionados.'
              : 'Mostrando parceiros vinculados a este plano. Digite 3+ caracteres para buscar outros parceiros.'}
          </Typography>
        </Box>
        {displayedPartners.length === 0 ? (
          <Typography className={classes.emptyMessage}>
            {searchTerm.length >= 3
              ? 'Nenhum parceiro encontrado para a busca.'
              : 'Nenhum parceiro vinculado a este plano.'}
          </Typography>
        ) : (
          <>
            <Box className={classes.tableContainer}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell style={{ width: '30%' }}>
                      <strong>Nome</strong>
                    </TableCell>
                    <TableCell style={{ width: '25%' }}>
                      <strong>Razão Social</strong>
                    </TableCell>
                    <TableCell style={{ width: '15%' }}>
                      <strong>Telefone</strong>
                    </TableCell>
                    <TableCell style={{ width: '10%' }}>
                      <strong>Status</strong>
                    </TableCell>
                    <TableCell style={{ width: '20%', textAlign: 'center' }}>
                      <strong>Ação</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedPartners.map(partner => {
                    const currentPlanName = getPartnerCurrentPlanName(partner);
                    const linkedToOther = isLinkedToOtherPlan(partner);
                    return (
                      <TableRow key={partner.id || partner.accountPublicId} className={getRowClassName(partner)}>
                        <TableCell>
                          <Typography variant="body2" style={{ fontWeight: 500 }}>
                            {partner.fullName || '-'}
                          </Typography>
                          {linkedToOther && currentPlanName && (
                            <Chip
                              size="small"
                              label={`Plano: ${currentPlanName}`}
                              className={classes.otherPlanChip}
                              style={{ marginTop: 4 }}
                            />
                          )}
                        </TableCell>
                        <TableCell>{partner.businessName || '-'}</TableCell>
                        <TableCell style={{ whiteSpace: 'nowrap' }}>
                          {formatPhone(partner.cellPhone || partner.phone)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={formatStatus(partner.status)}
                            size="small"
                            className={getStatusClass(partner.status)}
                          />
                        </TableCell>
                        <TableCell style={{ textAlign: 'center' }}>{renderPartnerAction(partner)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
            <Box className={classes.footer}>
              <Typography variant="body2" color="textSecondary">
                {searchTerm.length >= 3
                  ? `${displayedPartners.length} resultado(s) encontrado(s)`
                  : `${linkedPartners.length} parceiro(s) vinculado(s)`}
              </Typography>
              <TablePagination
                className={classes.paginationRoot}
                component="div"
                count={displayedPartners.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                labelRowsPerPage="Por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              />
            </Box>
          </>
        )}
      </>
    );
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle className={classes.dialogTitle} disableTypography>
          <Box>
            <Typography variant="h6">Gerenciar Parceiros</Typography>
            <Typography variant="body2" color="textSecondary">
              Plano: <strong>{planData?.name || 'Plano'}</strong>
            </Typography>
          </Box>
          <IconButton className={classes.closeButton} onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>{renderContent()}</DialogContent>
      </Dialog>

      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelConfirm}
        PaperProps={{
          style: {
            borderRadius: 16,
            padding: 0,
            maxWidth: 420,
            overflow: 'hidden',
          },
        }}
      >
        <Box style={{ padding: '28px 32px 24px', textAlign: 'center' }}>
          <Typography style={{ fontSize: 18, fontWeight: 600, color: '#1a1a1a', marginBottom: 8 }}>
            Alterar plano do parceiro
          </Typography>
          <Typography style={{ fontSize: 14, color: '#666', lineHeight: 1.6, marginBottom: 24 }}>
            O parceiro{' '}
            <strong style={{ color: '#333' }}>{partnerToConfirm ? getPartnerDisplayName(partnerToConfirm) : ''}</strong>{' '}
            será transferido para outro plano.
          </Typography>
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 20,
              marginBottom: 28,
            }}
          >
            <Box style={{ textAlign: 'center' }}>
              <Typography style={{ fontSize: 10, color: '#999', fontWeight: 600, marginBottom: 8, letterSpacing: 0.8 }}>
                PLANO ATUAL
              </Typography>
              <Box
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '10px 16px',
                  backgroundColor: '#fff',
                  borderRadius: 10,
                  border: '1px solid #e8e8e8',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                }}
              >
                <Typography style={{ fontSize: 14, fontWeight: 500, color: '#444' }}>
                  {partnerToConfirm ? getPartnerCurrentPlanName(partnerToConfirm) : ''}
                </Typography>
              </Box>
            </Box>
            <Box style={{ alignSelf: 'flex-end', marginBottom: 10 }}>
              <ArrowForwardIcon style={{ color: '#bdbdbd', fontSize: 22 }} />
            </Box>
            <Box style={{ textAlign: 'center' }}>
              <Typography style={{ fontSize: 10, color: '#999', fontWeight: 600, marginBottom: 8, letterSpacing: 0.8 }}>
                NOVO PLANO
              </Typography>
              <Box
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '10px 16px',
                  backgroundColor: 'rgba(76, 175, 80, 0.06)',
                  borderRadius: 10,
                  border: '1px solid rgba(76, 175, 80, 0.25)',
                }}
              >
                <Typography style={{ fontSize: 14, fontWeight: 600, color: '#2e7d32' }}>
                  {planData?.name || ''}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box
            style={{
              display: 'flex',
              gap: 12,
              justifyContent: 'center',
              borderTop: '1px solid #f0f0f0',
              paddingTop: 20,
              marginTop: 4,
            }}
          >
            <Button
              variant="outlined"
              onClick={handleCancelConfirm}
              style={{
                flex: 1,
                padding: '11px 20px',
                borderColor: '#e0e0e0',
                color: '#666',
                textTransform: 'none',
                fontWeight: 500,
                fontSize: 14,
                borderRadius: 10,
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmLink}
              style={{
                flex: 1,
                padding: '11px 20px',
                backgroundColor: '#FF5246',
                color: '#fff',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: 14,
                borderRadius: 10,
                boxShadow: '0 2px 8px rgba(255, 82, 70, 0.25)',
              }}
            >
              Confirmar
            </Button>
          </Box>
        </Box>
      </Dialog>

      <Snackbar
        open={showSuccessMessage}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MuiAlert onClose={handleCloseSnackbar} severity="success" elevation={6} variant="filled">
          Parceiro vinculado ao plano com sucesso!
        </MuiAlert>
      </Snackbar>

      <Snackbar
        open={showErrorMessage}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MuiAlert onClose={handleCloseSnackbar} severity="error" elevation={6} variant="filled">
          {errorMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
}

ModalGerenciarParceiros.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  planData: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    publicId: PropTypes.string,
    name: PropTypes.string,
  }),
};

ModalGerenciarParceiros.defaultProps = {
  planData: null,
};
